import { formatManualOrderErrorMessage } from "@/shared/lib/cf-api-error-messages";
import { config } from "@/shared/lib/config";
import { ApiRequestError, fetcher } from "@/shared/lib/fetcher";
import type {
  CatalogListResponse,
  FeedCommentRow,
  FeedCustomerRow,
  FeedDetailPageResponse,
  FeedDetailResponse,
  FeedDetailSummaryResponse,
  FeedOrderSpecialWork,
  FeedProductMonitorProduct,
  FeedProductMonitorTotals,
  FeedProductSaleRow,
  FeedProductSalesProduct,
  FeedSessionProductGroup,
  FeedSessionProductRow,
  FeedsListResponse,
  LinkCommentMessengerProfileResponse,
  MessengerProfileCandidatesResponse,
  PostProductsContextResponse,
  ProductVariantDefaultCodesResponse,
  ReverseVariantCfStockResponse,
  SessionProductItem,
  SyncFeedsResponse,
  ToggleFeedOrderingResponse,
} from "./types";

const base = `${config.apiUrl}/feeds`;
const postProductsBase = `${base}/post-products`;

export const feedsApi = {
  list(params?: {
    pageId?: string;
    feedType?: "post" | "live";
    q?: string;
    status?: "active" | "draft" | "closed" | "inactive";
    limit?: number;
    offset?: number;
  }): Promise<FeedsListResponse> {
    const q = new URLSearchParams();
    if (params?.pageId) q.set("pageId", params.pageId);
    if (params?.feedType) q.set("feedType", params.feedType);
    if (params?.q) q.set("q", params.q);
    if (params?.status) q.set("status", params.status);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return fetcher.get(`${base}${qs ? `?${qs}` : ""}`);
  },

  get(id: string): Promise<FeedDetailResponse> {
    return fetcher.get(`${base}/${id}`);
  },

  sync(pageId?: string): Promise<SyncFeedsResponse> {
    const q = pageId ? `?pageId=${encodeURIComponent(pageId)}` : "";
    return fetcher.post(`${base}/sync${q}`);
  },

  setOrdering(
    id: string,
    enabled: boolean,
  ): Promise<ToggleFeedOrderingResponse> {
    return fetcher.patch(`${base}/${id}/ordering`, { enabled });
  },

  catalog(params?: {
    pageId?: string;
    warehouseId?: string;
    q?: string;
    limit?: number;
    offset?: number;
    groupByProduct?: boolean;
    includeUnavailable?: boolean;
    readiness?: "ready" | "zero" | "preorder";
    variantIds?: string[];
  }): Promise<CatalogListResponse> {
    const q = new URLSearchParams();
    if (params?.pageId) q.set("pageId", params.pageId);
    if (params?.warehouseId) q.set("warehouseId", params.warehouseId);
    if (params?.q) q.set("q", params.q);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    if (params?.groupByProduct) q.set("groupByProduct", "true");
    if (params?.includeUnavailable) q.set("includeUnavailable", "true");
    if (params?.readiness) q.set("readiness", params.readiness);
    if (params?.variantIds?.length)
      q.set("variantIds", params.variantIds.join(","));
    const qs = q.toString();
    return fetcher.get(`${postProductsBase}/catalog${qs ? `?${qs}` : ""}`);
  },

  listPosWarehouses(): Promise<{
    items: import("./types").PosWarehouseItem[];
  }> {
    return fetcher.get(`${postProductsBase}/warehouses`);
  },

  defaultCodes(params: {
    q?: string;
    limit?: number;
    offset?: number;
    groupByProduct?: boolean;
  }): Promise<ProductVariantDefaultCodesResponse> {
    const q = new URLSearchParams();
    if (params.q) q.set("q", params.q);
    if (params.limit != null) q.set("limit", String(params.limit));
    if (params.offset != null) q.set("offset", String(params.offset));
    if (params.groupByProduct) q.set("groupByProduct", "true");
    const qs = q.toString();
    return fetcher.get(
      `${postProductsBase}/default-codes${qs ? `?${qs}` : ""}`,
    );
  },

  setDefaultCode(body: {
    variantId: string;
    cfCode: string;
  }): Promise<{ item: ProductVariantDefaultCodesResponse["items"][number] }> {
    return fetcher.patch(
      `${postProductsBase}/default-codes/${body.variantId}`,
      {
        cfCode: body.cfCode,
      },
    );
  },

  reverseVariantCfStock(
    variantId: string,
  ): Promise<ReverseVariantCfStockResponse> {
    return fetcher.post(
      `${postProductsBase}/default-codes/${variantId}/reverse-stock`,
      {},
    );
  },

  postProductsContext(feedId: string): Promise<PostProductsContextResponse> {
    return fetcher.get(
      `${postProductsBase}/context?feedId=${encodeURIComponent(feedId)}`,
    );
  },

  addSessionProduct(body: {
    feedId: string;
    variantId: string;
    cfCode?: string;
    price?: number;
    allocatedQuantity?: number;
    allocationMode?: "limit" | "unlimited";
    options?: Array<{
      adminOptionDefinitionId: string;
      surchargeLak: number;
      isAutoAttach?: boolean;
      requiresCustomerInput?: boolean;
      sortOrder?: number;
    }>;
    discountPercent?: number | null;
    discountFixedLak?: number | null;
  }): Promise<{ item: SessionProductItem }> {
    return fetcher.post(`${postProductsBase}/session-products`, body);
  },

  addSessionProductsBySelection(body: {
    feedId: string;
    allocationMode?: "limit" | "unlimited";
    allocatedQuantity?: number;
    selection:
      | { mode: "selected"; variantIds: string[] }
      | {
          mode: "all_matching";
          q?: string;
          excludedVariantIds?: string[];
        };
    options?: Array<{
      adminOptionDefinitionId: string;
      surchargeLak: number;
      isAutoAttach?: boolean;
      requiresCustomerInput?: boolean;
      sortOrder?: number;
    }>;
    discountPercent?: number | null;
    discountFixedLak?: number | null;
  }): Promise<{
    added: number;
    skipped: number;
    failed: Array<{ variantId: string; code: string }>;
    items: SessionProductItem[];
  }> {
    return fetcher.post(`${postProductsBase}/session-products/bulk`, body);
  },

  listPreAddOptionCandidates(
    feedId: string,
    variantId?: string | null,
  ): Promise<{
    items: Array<{
      adminOptionDefinitionId: string;
      code: string;
      label: string;
      posDefaultSurchargeLak: number;
      hasRequiredFields: boolean;
      alreadyLinked: boolean;
    }>;
  }> {
    const params = new URLSearchParams({ feedId });
    if (variantId?.trim()) params.set("variantId", variantId.trim());
    return fetcher.get(
      `${postProductsBase}/option-candidates?${params.toString()}`,
    );
  },

  patchPostSessionProduct(
    id: string,
    body: {
      allocatedQuantity?: number;
      cfCode?: string;
      price?: number;
      allocationMode?: "limit" | "unlimited";
      discountPercent?: number | null;
      discountFixedLak?: number | null;
      saleEnabled?: boolean;
    },
  ): Promise<{ item: SessionProductItem }> {
    return fetcher.patch(`${postProductsBase}/session-products/${id}`, body);
  },

  patchPostSessionProductsBulk(body: {
    ids: string[];
    patch: {
      allocatedQuantity?: number;
      allocationMode?: "limit" | "unlimited";
      discountPercent?: number | null;
      discountFixedLak?: number | null;
      saleEnabled?: boolean;
    };
  }): Promise<{
    updated: number;
    failed: Array<{ id: string; code: string }>;
    items: SessionProductItem[];
  }> {
    return fetcher.patch(`${postProductsBase}/session-products/bulk`, body);
  },

  removeSessionProductsBulk(body: { ids: string[] }): Promise<{
    removed: number;
    failed: Array<{ id: string; code: string }>;
  }> {
    return fetcher.post(
      `${postProductsBase}/session-products/bulk-remove`,
      body,
    );
  },

  putSessionProductOptionsBulk(body: {
    feedId: string;
    sessionProductIds: string[];
    items: Array<{
      adminOptionDefinitionId: string;
      surchargeLak: number;
      isAutoAttach?: boolean;
      requiresCustomerInput?: boolean;
      sortOrder?: number;
    }>;
    discountPercent?: number | null;
    discountFixedLak?: number | null;
  }): Promise<{
    updated: number;
    failed: Array<{ id: string; code: string }>;
  }> {
    return fetcher.put(
      `${postProductsBase}/session-products/options/bulk`,
      body,
    );
  },

  patchSessionWarehouse(body: {
    feedId: string;
    warehouseId: string;
  }): Promise<{ retailStockWarehouseId: string }> {
    return fetcher.patch(`${postProductsBase}/session-warehouse`, body);
  },

  removeSessionProduct(id: string): Promise<{ ok: boolean }> {
    return fetcher.delete(`${postProductsBase}/session-products/${id}`);
  },

  confirmPost(feedId: string): Promise<{ ok: true }> {
    return fetcher.post(`${postProductsBase}/confirm`, { feedId });
  },

  detailSummary(feedId: string): Promise<FeedDetailSummaryResponse> {
    return fetcher.get(`${base}/${feedId}/detail`);
  },

  detailCustomers(
    feedId: string,
    params?: { q?: string; limit?: number; offset?: number },
  ): Promise<{
    customers: FeedCustomerRow[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const q = new URLSearchParams();
    if (params?.q) q.set("q", params.q);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    const qs = q.toString();
    return fetcher.get(`${base}/${feedId}/customers${qs ? `?${qs}` : ""}`);
  },

  adjustOrderQuantity(
    feedId: string,
    orderId: string,
    body: { quantity: number },
    idempotencyKey: string,
  ): Promise<{
    order: {
      orderId: string;
      quantity: number;
      releasedQuantity: number;
      totalPrice: number;
      status: "pending" | "cancelled";
    };
    replayed: boolean;
  }> {
    return fetcher.patchWithHeaders(
      `${base}/${feedId}/orders/${orderId}/quantity`,
      body,
      { "Idempotency-Key": idempotencyKey },
      { silentError: true },
    );
  },

  detailProductSales(
    feedId: string,
    params: { limit: number; offset: number; paymentState?: "paid" },
  ): Promise<
    FeedDetailPageResponse<FeedProductSaleRow> & {
      products: FeedProductSalesProduct[];
    }
  > {
    const q = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
      groupByProduct: "true",
    });
    if (params.paymentState) q.set("paymentState", params.paymentState);
    return fetcher.get(`${base}/${feedId}/product-sales?${q}`);
  },

  detailProductMonitor(
    feedId: string,
    params: { limit: number; offset: number; productIds?: string[] },
  ): Promise<{
    products: FeedProductMonitorProduct[];
    totals: FeedProductMonitorTotals;
    total: number;
    limit: number;
    offset: number;
  }> {
    const q = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
    });
    if (params.productIds !== undefined)
      q.set("productIds", params.productIds.join(","));
    return fetcher.get(`${base}/${feedId}/product-monitor?${q}`);
  },

  detailSessionProducts(
    feedId: string,
    params: {
      limit: number;
      offset: number;
      saleEnabled?: boolean;
      groupByProduct?: boolean;
      q?: string;
    },
  ): Promise<
    FeedDetailPageResponse<FeedSessionProductRow> & {
      products?: FeedSessionProductGroup[];
    }
  > {
    const q = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
    });
    if (params.saleEnabled != null) {
      q.set("saleEnabled", String(params.saleEnabled));
    }
    if (params.groupByProduct) {
      q.set("groupByProduct", "true");
    }
    if (params.q) q.set("q", params.q);
    return fetcher.get(`${base}/${feedId}/session-products?${q}`);
  },

  getOrderSpecialWork(
    feedId: string,
    orderId: string,
  ): Promise<FeedOrderSpecialWork> {
    return fetcher.get(`${base}/${feedId}/orders/${orderId}/special-work`);
  },

  updateOrderSpecialWork(
    feedId: string,
    orderId: string,
    units: Array<{
      unitIndex: number;
      name?: string | null;
      number?: string | null;
    }>,
  ): Promise<FeedOrderSpecialWork> {
    return fetcher.patch(`${base}/${feedId}/orders/${orderId}/special-work`, {
      units,
    });
  },

  getSessionPromo(feedId: string): Promise<{
    promo: {
      sessionId: string;
      listDiscountPercent: number | null;
      listDiscountFixedLak: number | null;
      freeShipping: boolean;
      shippingFeeLak: number | null;
      printOptions: Array<{
        code: string;
        adminOptionDefinitionId: string;
        label: string;
        posDefaultSurchargeLak: number;
        enabled: boolean;
        surchargeLak: number;
        isAutoAttach: boolean;
        requiresCustomerInput: boolean;
      }>;
    };
  }> {
    return fetcher.get(`${base}/${feedId}/session-promo`);
  },

  patchSessionPromo(
    feedId: string,
    body: {
      listDiscountPercent?: number | null;
      listDiscountFixedLak?: number | null;
      freeShipping?: boolean;
      shippingFeeLak?: number | null;
      printOptions?: Array<{
        code: string;
        enabled: boolean;
        surchargeLak: number;
        isAutoAttach?: boolean;
        requiresCustomerInput?: boolean;
      }>;
    },
  ): Promise<{
    promo: {
      sessionId: string;
      listDiscountPercent: number | null;
      listDiscountFixedLak: number | null;
      freeShipping: boolean;
      shippingFeeLak: number | null;
      printOptions: Array<{
        code: string;
        adminOptionDefinitionId: string;
        label: string;
        posDefaultSurchargeLak: number;
        enabled: boolean;
        surchargeLak: number;
        isAutoAttach: boolean;
        requiresCustomerInput: boolean;
      }>;
    };
  }> {
    return fetcher.patch(`${base}/${feedId}/session-promo`, body);
  },

  getPrintFeeDefaults(): Promise<{
    items: Array<{ optionCode: string; defaultSurchargeLak: number }>;
  }> {
    return fetcher.get(`${base}/print-fee-defaults`);
  },

  putPrintFeeDefaults(
    items: Array<{ optionCode: string; defaultSurchargeLak: number }>,
  ): Promise<{
    items: Array<{ optionCode: string; defaultSurchargeLak: number }>;
  }> {
    return fetcher.put(`${base}/print-fee-defaults`, { items });
  },

  listSessionProductOptions(
    feedId: string,
    spId: string,
  ): Promise<{
    items: Array<{
      id: string;
      sessionProductId: string;
      adminOptionDefinitionId: string;
      labelSnapshot: string;
      posDefaultSurchargeLak: number;
      surchargeLak: number;
      isAutoAttach: boolean;
      requiresCustomerInput: boolean;
      sortOrder: number;
    }>;
  }> {
    return fetcher.get(`${base}/${feedId}/session-products/${spId}/options`);
  },

  listSessionProductOptionCandidates(
    feedId: string,
    spId: string,
  ): Promise<{
    items: Array<{
      adminOptionDefinitionId: string;
      code: string;
      label: string;
      posDefaultSurchargeLak: number;
      hasRequiredFields: boolean;
      alreadyLinked: boolean;
    }>;
  }> {
    return fetcher.get(
      `${base}/${feedId}/session-products/${spId}/option-candidates`,
    );
  },

  putSessionProductOptions(
    feedId: string,
    spId: string,
    items: Array<{
      adminOptionDefinitionId: string;
      surchargeLak: number;
      isAutoAttach?: boolean;
      requiresCustomerInput?: boolean;
      sortOrder?: number;
    }>,
  ): Promise<{
    items: Array<{
      id: string;
      adminOptionDefinitionId: string;
      labelSnapshot: string;
      surchargeLak: number;
    }>;
  }> {
    return fetcher.put(`${base}/${feedId}/session-products/${spId}/options`, {
      items,
    });
  },

  patchFeedSessionProduct(
    feedId: string,
    spId: string,
    body: {
      saleEnabled?: boolean;
      allocatedQuantity?: number | null;
      allocationMode?: "limit" | "unlimited";
      discountPercent?: number | null;
      discountFixedLak?: number | null;
    },
  ): Promise<{ items: FeedSessionProductRow[] }> {
    return fetcher.patch(`${base}/${feedId}/session-products/${spId}`, body);
  },

  detailComments(
    feedId: string,
    params: { q?: string; limit: number; offset: number },
  ): Promise<{
    comments: FeedCommentRow[];
    total: number;
    messengerIdentityPendingTotal: number;
    limit: number;
    offset: number;
  }> {
    const q = new URLSearchParams({
      limit: String(params.limit),
      offset: String(params.offset),
    });
    if (params.q) q.set("q", params.q);
    return fetcher.get(`${base}/${feedId}/comments?${q}`);
  },

  messengerProfiles(
    feedId: string,
    commentLogId: string,
    params?: { q?: string; limit?: number; offset?: number },
  ): Promise<MessengerProfileCandidatesResponse> {
    const q = new URLSearchParams();
    if (params?.q) q.set("q", params.q);
    if (params?.limit != null) q.set("limit", String(params.limit));
    if (params?.offset != null) q.set("offset", String(params.offset));
    return fetcher.get(
      `${base}/${feedId}/orders/comments/${commentLogId}/messenger-profiles?${q}`,
    );
  },

  linkCommentMessengerProfile(
    feedId: string,
    commentLogId: string,
    profileId: string,
  ): Promise<LinkCommentMessengerProfileResponse> {
    return fetcher.post(
      `${base}/${feedId}/orders/comments/${commentLogId}/messenger-profile`,
      { profileId },
    );
  },

  async createManualOrder(
    feedId: string,
    body: {
      commentLogId?: string;
      cfCode: string;
      quantity: number;
      specialWorkUnits?: Array<{
        unitIndex: number;
        name?: string | null;
        number?: string | null;
      }>;
    },
  ): Promise<{
    order: {
      orderId: string;
      customerName: string | null;
      cfCode: string;
      quantity: number;
      requestedQuantity: number;
      partial: boolean;
      linkedExisting?: boolean;
      incrementedExisting?: boolean;
      newOrderAfterPosLocked?: boolean;
      shopOrderCode?: string | null;
      totalPrice: number;
      dmStatus?: "sent" | "failed" | "skipped";
      dmSkipReason?: string;
    };
  }> {
    type ManualOrderPayload = {
      order?: {
        orderId: string;
        customerName: string | null;
        cfCode: string;
        quantity: number;
        requestedQuantity: number;
        partial: boolean;
        totalPrice: number;
      };
      code?: string;
      message?: string;
      error?: string;
    };

    try {
      const payload = await fetcher.post<ManualOrderPayload>(
        `${base}/${feedId}/orders/manual`,
        body,
        { silentError: true },
      );
      if (!payload.order) {
        const reason = formatManualOrderErrorMessage("ORDER_CREATE_FAILED");
        throw Object.assign(new Error(reason), { code: "ORDER_CREATE_FAILED" });
      }
      return { order: payload.order };
    } catch (error) {
      if (!(error instanceof ApiRequestError)) throw error;
      const reason = formatManualOrderErrorMessage(error.code);
      throw Object.assign(new Error(reason), {
        code: error.code,
        status: error.status,
      });
    }
  },

  createManualOrders(
    feedId: string,
    body: {
      commentLogIds: string[];
      cfCode: string;
      quantity: number;
      specialWorkUnits?: Array<{
        unitIndex: number;
        name?: string | null;
        number?: string | null;
      }>;
    },
  ): Promise<{
    orders: Array<{ commentLogId: string; order: { orderId: string } }>;
    failed: Array<{ commentLogId: string; code: string }>;
  }> {
    return fetcher.post(`${base}/${feedId}/orders/manual`, body);
  },
};
